import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import Seo from "./seo/Seo";
import Nav from "./sections/Nav";
import Footer from "./sections/Footer";
import ContactDock from "./sections/ContactDock";
import Home from "./pages/Home";
import CustomCursor from "./components/CustomCursor";
import DynamicSEO from "./components/DynamicSEO";
import { isAdminAuthed } from "./lib/adminAuth";
import { person, projects as bundledProjects } from "./data/profile";
import { landingRoutes } from "./routes/publicRoutes";
import { buildPageGraph } from "./seo/structuredData";

/* Admin + blog are separate concerns from the portfolio itself and should
   never be in the homepage bundle. */
/* three.js is ~150kB gzipped. Keeping it out of the main bundle means the
   hero text paints before the 3D engine is even fetched. */
const ScrollScene = lazy(() => import("./three/ScrollScene"));

const AdminLogin = lazy(() => import("./components/AdminLogin"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const BlogsPage = lazy(() => import("./pages/BlogsPage"));
const SingleBlogPage = lazy(() => import("./pages/SingleBlogPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));

/**
 * Public shell: 3D backdrop + nav + footer around any public route.
 *
 * Layering is explicit rather than implicit. The scene is a fixed element at
 * z-0 and all content sits in a sibling layer at z-10. The obvious
 * alternative — a negative z-index on the canvas — silently fails: a
 * negatively-stacked fixed child escapes to the nearest ancestor that forms
 * a stacking context, and any opaque background between here and there
 * paints straight over it. Two explicit layers can't drift like that.
 *
 * No background colour here on purpose: <body> owns the ground colour, so
 * nothing opaque can ever get between the viewer and the canvas.
 */
function Shell({ children, withScene = true }) {
  return (
    <div className="grain relative min-h-screen">
      {withScene && (
        <Suspense fallback={null}>
          <ScrollScene />
        </Suspense>
      )}
      <div className="relative z-10">
        <Nav />
        {children}
        <Footer />
      </div>
      <ContactDock />
    </div>
  );
}

function RequireAdmin({ children }) {
  if (!isAdminAuthed()) return <Navigate to="/admin" replace />;
  return children;
}

function Loading() {
  return (
    <div className="grid min-h-screen place-items-center bg-ink-950">
      <div className="flex flex-col items-center gap-4">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-white/10 border-t-brand-400" />
        <span className="font-mono text-[0.6rem] uppercase tracking-label text-slate-600">
          Loading
        </span>
      </div>
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <>
      {/* Blog routes swap in their own head via DynamicSEO. */}
      <DynamicSEO />

      {/* Skip link — the page is long and the nav is the first thing in the
          tab order, so keyboard users need a way past it. */}
      <a
        href="#main"
        className="sr-only-seo focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-brand-400 focus:px-5 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-ink-950"
        style={{ clip: "auto" }}
      >
        Skip to content
      </a>

      {!isAdmin && <CustomCursor />}

      <Suspense fallback={<Loading />}>
        <Routes>
          <Route
            path="/"
            element={
              <>
                <Seo path="/" />
                <Shell>
                  <Home />
                </Shell>
              </>
            }
          />

          <Route
            path="/projects"
            element={
              <>
                <Seo
                  path="/projects"
                  type="website"
                  title={`All projects by ${person.name} — MERN, AI & 3D web`}
                  description={`Every production platform ${person.name} has built: multi-tenant SaaS, AI learning products, WebGL commerce and internal tooling. Problem, approach and result for each.`}
                  graph={buildPageGraph({
                    path: "/projects",
                    type: "CollectionPage",
                    name: `All projects by ${person.name}`,
                    description: `Production web platforms built by ${person.name}.`,
                    trail: [
                      { name: "Home", url: "/" },
                      { name: "Projects", url: "/projects" },
                    ],
                    items: bundledProjects,
                  })}
                />
                <Shell withScene={false}>
                  <main id="main" className="pt-24">
                    <ProjectsPage />
                  </main>
                </Shell>
              </>
            }
          />

          {/* SEO landing pages, generated from src/data/landingPages.json.
              One definition per page keeps canonical/OG/JSON-LD in lockstep. */}
          {landingRoutes.map((r) => (
            <Route
              key={r.path}
              path={r.path}
              element={
                <Shell withScene={false}>
                  <main id="main" className="pt-24">
                    {r.element}
                  </main>
                </Shell>
              }
            />
          ))}

          <Route
            path="/projects/:slug"
            element={
              <Shell withScene={false}>
                <main id="main" className="pt-24">
                  <ProjectDetailPage />
                </main>
              </Shell>
            }
          />

          <Route
            path="/blogs"
            element={
              <>
                <Seo
                  path="/blogs"
                  type="website"
                  title={`Writing on web engineering | ${person.name}`}
                  description={`Notes from ${person.name} on MERN architecture, multi-tenant SaaS, production AI integration and 3D web performance.`}
                  graph={buildPageGraph({
                    path: "/blogs",
                    type: "CollectionPage",
                    name: `Writing by ${person.name}`,
                    description: "Notes on web engineering.",
                    trail: [
                      { name: "Home", url: "/" },
                      { name: "Blog", url: "/blogs" },
                    ],
                  })}
                />
                <Shell withScene={false}>
                  <main id="main" className="pt-24">
                    <BlogsPage />
                  </main>
                </Shell>
              </>
            }
          />

          <Route
            path="/blogs/:id"
            element={
              <Shell withScene={false}>
                <main id="main" className="pt-24">
                  <SingleBlogPage />
                </main>
              </Shell>
            }
          />

          <Route
            path="/contact"
            element={
              <>
                <Seo
                  path="/contact"
                  type="website"
                  title={`Contact ${person.name} — Full Stack Developer & Technical Lead`}
                  description={`Get in touch with ${person.name} for senior full stack roles, MERN product work, AI integration or 3D web projects. Email ${person.email}.`}
                  graph={buildPageGraph({
                    path: "/contact",
                    type: "ContactPage",
                    name: `Contact ${person.name}`,
                    description: `Get in touch with ${person.name}.`,
                    trail: [
                      { name: "Home", url: "/" },
                      { name: "Contact", url: "/contact" },
                    ],
                  })}
                />
                <Shell withScene={false}>
                  <main id="main" className="pt-24">
                    <ContactPage />
                  </main>
                </Shell>
              </>
            }
          />

          <Route
            path="/admin"
            element={
              <>
                <Seo path="/admin" noindex title="Admin" />
                <AdminLogin />
              </>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <>
                <Seo path="/admin/dashboard" noindex title="Dashboard" />
                <RequireAdmin>
                  <AdminDashboard />
                </RequireAdmin>
              </>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}
